document.addEventListener('DOMContentLoaded', () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const sectionScrollThresshold = 0.20 // 20% - default threshold
    const bannerScrollThresshold = 0.20 // 20%

    function isHomepage() {
        const meta = document.querySelector('meta[name="is_homepage"]')
        return meta?.getAttribute('content') === 'yes'
    }

    function setupHomepageHeroScrollClass() {
        if (!isHomepage()) return

        const hero = document.querySelector('[data-home-hero]')
        if (!hero) return

        let threshold = 0
        let ticking = false

        function setScrolledState() {
            const y = window.scrollY || document.documentElement.scrollTop || 0
            document.body.classList.toggle('is-scrolled', y >= threshold)
        }

        function computeThreshold() {
            const rect = hero.getBoundingClientRect()
            const top = rect.top + (window.scrollY || document.documentElement.scrollTop || 0)
            const height = hero.offsetHeight || rect.height || 0
            threshold = top + height * bannerScrollThresshold
            setScrolledState()
        }

        function onScroll() {
            if (ticking) return
            ticking = true
            requestAnimationFrame(() => {
                ticking = false
                setScrolledState()
            })
        }

        window.addEventListener('scroll', onScroll, { passive: true })
        window.addEventListener('resize', computeThreshold)
        window.addEventListener('load', computeThreshold)
        computeThreshold()
    }

    function setupMobileMenuBehavior() {
        const toggle = document.getElementById('header-menu-toggle')
        if (!toggle) return

        const menu = document.getElementById('header-menu')
        const button = document.getElementById('header-menu-button')
        const mq = window.matchMedia('(min-width: 768px)')
        let lastFocused = null

        const focusablesSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

        function isMobile() {
            return !mq.matches
        }

        function getMenuFocusables() {
            if (!menu) return []
            return Array.from(menu.querySelectorAll(focusablesSelector)).filter((el) => {
                if (el.hasAttribute('disabled')) return false
                return el.offsetParent !== null
            })
        }

        function setExpanded(isOpen) {
            if (button) button.setAttribute('aria-expanded', String(isOpen))
        }

        function syncLock() {
            const isOpen = !!toggle.checked
            if (!isMobile()) {
                document.body.classList.remove('scroll-lock')
                if (toggle.checked) toggle.checked = false
                if (menu) menu.hidden = false
                setExpanded(false)
                return
            }

            if (isOpen) {
                lastFocused = document.activeElement
                if (menu) menu.hidden = false
                document.body.classList.add('scroll-lock')
                setExpanded(true)
                const focusables = getMenuFocusables()
                if (focusables[0]) focusables[0].focus()
                else if (menu && typeof menu.focus === 'function') menu.focus()
            } else {
                const focusWasInsideMenu = !!(menu && menu.contains(document.activeElement))
                if (focusWasInsideMenu) {
                    if (button && typeof button.focus === 'function') button.focus()
                    else if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus()
                }
                document.body.classList.remove('scroll-lock')
                setExpanded(false)
                if (menu) menu.hidden = true
                lastFocused = null
            }
        }

        function closeMenu() {
            if (!toggle.checked) return
            toggle.checked = false
            syncLock()
        }

        toggle.addEventListener('change', syncLock)

        if (button) {
            button.addEventListener('click', () => {
                toggle.checked = !toggle.checked
                syncLock()
            })
        }

        if (menu) {
            menu.addEventListener('click', (e) => {
                const link = e.target?.closest?.('a[href]')
                if (!link) return
                closeMenu()
            })
        }

        window.addEventListener('keydown', (e) => {
            if (!isMobile()) return
            if (e.key === 'Escape') return closeMenu()
            if (e.key !== 'Tab') return
            if (!toggle.checked) return

            const focusables = [button, ...getMenuFocusables()].filter(Boolean)
            if (!focusables.length) return

            const first = focusables[0]
            const last = focusables[focusables.length - 1]
            const active = document.activeElement

            if (e.shiftKey && active === first) {
                e.preventDefault()
                last.focus()
                return
            }

            if (!e.shiftKey && active === last) {
                e.preventDefault()
                first.focus()
            }
        })

        try {
            const onChange = () => {
                if (mq.matches) closeMenu()
                syncLock()
            }
            if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange)
            else if (typeof mq.addListener === 'function') mq.addListener(onChange)
        } catch { }

        syncLock()
    }

    function setupHeaderDropdownAria() {
        const buttons = Array.from(document.querySelectorAll('.header-dropdown-toggle[aria-controls]'))
        if (!buttons.length) return

        function syncButton(btn) {
            const id = btn.getAttribute('aria-controls')
            const menu = id ? document.getElementById(id) : null
            if (!menu) return
            const isHidden = menu.classList.contains('hidden') || menu.hidden
            btn.setAttribute('aria-expanded', String(!isHidden))
        }

        buttons.forEach((btn) => {
            syncButton(btn)
            btn.addEventListener('click', () => {
                requestAnimationFrame(() => syncButton(btn))
            })
        })
    }

    function setupSectionReveal() {
        if (prefersReducedMotion) return

        const sections = Array.from(document.querySelectorAll('section[data-reveal]'))
        if (!sections.length) return

        const thresholds = sections.map((el) => {
            const thresholdEl = el.getAttribute('data-scroll-threshold')
            return thresholdEl ? Number(thresholdEl) : sectionScrollThresshold
        })
        if (!sections.length) return

        if (!('IntersectionObserver' in window)) {
            sections.forEach((el) => el.classList.add('is-visible'))
            return
        }

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return
                if (entry.intersectionRatio < thresholds[sections.indexOf(entry.target)]) return
                entry.target.classList.add('is-visible')
                observer.unobserve(entry.target)
            })
        }, { threshold: thresholds })

        sections.forEach((el) => observer.observe(el))
    }

    function setupInsightTableOfContents() {
        const articles = Array.from(document.querySelectorAll('.js-insight-article'))
        if (!articles.length) return

        const slugify = (value) => value
            .toLowerCase()
            .trim()
            .replace(/&/g, ' and ')
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')

        articles.forEach((article) => {
            const headings = Array.from(article.querySelectorAll('.prose h2'))
            if (!headings.length) return

            const usedIds = new Set(
                Array.from(article.querySelectorAll('[id]'))
                    .map((el) => el.id)
                    .filter(Boolean)
            )

            headings.forEach((heading) => {
                const tocTitle = heading.dataset.tocTitle?.trim() || heading.textContent.trim()

                if (heading.id) {
                    usedIds.add(heading.id)
                    return
                }

                const baseId = slugify(tocTitle) || 'section'
                let candidate = baseId
                let counter = 2

                while (usedIds.has(candidate)) {
                    candidate = `${baseId}-${counter}`
                    counter += 1
                }

                heading.id = candidate
                usedIds.add(candidate)
            })

            const tocHeading = Array.from(article.querySelectorAll('.prose h3'))
                .find((heading) => heading.textContent.trim().toLowerCase() === 'table of contents')

            if (!tocHeading) return

            const tocSource = tocHeading.nextElementSibling
            if (!tocSource || tocSource.tagName !== 'P') return

            const list = document.createElement('ol')
            list.className = 'toc-list'

            headings.forEach((heading) => {
                const item = document.createElement('li')
                const link = document.createElement('a')
                const permalink = heading.querySelector('[data-heading-permalink]')?.getAttribute('href') || `#${heading.id}`

                link.href = permalink
                link.textContent = heading.dataset.tocTitle?.trim() || heading.textContent.trim()
                item.appendChild(link)
                list.appendChild(item)
            })

            tocHeading.classList.add('toc-heading')
            tocSource.innerHTML = ''
            tocSource.className = 'toc-card not-prose'
            tocSource.appendChild(list)
        })
    }

    setupHomepageHeroScrollClass()
    setupMobileMenuBehavior()
    setupHeaderDropdownAria()
    setupSectionReveal()
    setupInsightTableOfContents()
})
